import { useEffect, useState } from "react";
import { getCityByStateId } from "../api/api";

/**
 * Loads the cities that belong to one province.
 *
 * Argentina's catalog holds thousands of localities, so no screen may fetch the
 * full city list: city options always depend on the selected province.
 *
 * - Empty `stateId` -> no request, `cities` is `[]` (callers disable / hide the city selector).
 * - Changing `stateId` drops the previous list immediately and ignores late responses
 *   from the previous province (request race).
 * - A failed request yields `[]`; the selectors degrade to "no options" rather than throwing.
 *
 * @param {string} stateId Province ObjectId, or "" when none is selected.
 * @returns {{ cities: Array<{_id: string, name: string}>, loading: boolean }} cities sorted by name (es).
 */
const useCitiesByState = (stateId) => {
	const [state, setState] = useState({ forStateId: "", cities: [], loading: false });

	useEffect(() => {
		if (!stateId) return undefined;
		let cancelled = false;
		setState({ forStateId: stateId, cities: [], loading: true });
		getCityByStateId({ stateId })
			.then((res) => {
				if (cancelled) return;
				const list = Array.isArray(res?.data?.info) ? res.data.info.slice() : [];
				list.sort((a, b) =>
					String(a?.name || "").localeCompare(String(b?.name || ""), "es", { sensitivity: "base" }),
				);
				setState({ forStateId: stateId, cities: list, loading: false });
			})
			.catch(() => {
				if (!cancelled) setState({ forStateId: stateId, cities: [], loading: false });
			});
		return () => {
			cancelled = true;
		};
	}, [stateId]);

	// Never expose a list that belongs to a different province than the one requested.
	if (!stateId || state.forStateId !== stateId) return { cities: [], loading: Boolean(stateId) };
	return { cities: state.cities, loading: state.loading };
};

export default useCitiesByState;
